import {
  AreaChartOutlined,
  BarChartOutlined,
  BlockOutlined,
  DotChartOutlined,
  HolderOutlined,
  LineChartOutlined,
  PieChartOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import type { DragEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { CHART_DND_MIME } from "@/warRoom/canvas/layout";
import { datasetByKey } from "@/warRoom/datasets/registry";
import { formsForDataset } from "@/warRoom/graph/chartForms";
import type { ChartType } from "@/warRoom/graph/spec";
import { useGraphStore } from "@/warRoom/store/useGraphStore";

const { Text } = Typography;

const ICONS: Record<ChartType, ReactNode> = {
  line: <LineChartOutlined />,
  area: <AreaChartOutlined />,
  column: <BarChartOutlined />,
  bar: <BarChartOutlined rotate={90} />,
  stackedColumn: <BlockOutlined />,
  stackedBar: <BlockOutlined rotate={90} />,
  groupedColumn: <BarChartOutlined />,
  pie: <PieChartOutlined />,
  scatter: <DotChartOutlined />,
  heatmap: <TableOutlined />,
};

/**
 * 第 ② 步：圖表清單，同時是拖曳來源。
 * 拖到畫布上放在游標處；點一下則放到畫布的下一個空位 —— 兩條路都會多一張圖，
 * 不會取代畫布上已經有的圖。只列這份資料畫得出來的型別。
 */
export default function ChartPanel() {
  const { t } = useTranslation();
  const activeDatasetKey = useGraphStore((s) => s.activeDatasetKey);
  const pickChart = useGraphStore((s) => s.pickChart);
  const dataset = activeDatasetKey ? datasetByKey(activeDatasetKey) : undefined;

  if (!dataset) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t("warRoom.chartPanel.pickDatasetFirst")}
      </Text>
    );
  }

  const forms = formsForDataset(dataset);

  const onDragStart = (e: DragEvent, type: ChartType) => {
    e.dataTransfer.setData(CHART_DND_MIME, type);
    // Safari 對自訂 MIME 比較挑，補一份 text/plain 當備援
    e.dataTransfer.setData("text/plain", type);
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div>
      <Text type="secondary" className="panel-hint">
        {t("warRoom.chartPanel.hint", {
          dataset: dataset.label,
          count: forms.length,
        })}
      </Text>

      {forms.map((form) => (
        /*
         * 這一列刻意不是 <button>：Chrome 不會從表單控制項發動原生 HTML5 拖曳，
         * button 上的 draggable 按下去根本不會起拖。改成 div + role/tabIndex/鍵盤，
         * 點擊與鍵盤操作維持原樣。
         */
        <div
          key={form.type}
          role="button"
          tabIndex={0}
          draggable
          onDragStart={(e) => onDragStart(e, form.type)}
          onClick={() => pickChart(form.type)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              pickChart(form.type);
            }
          }}
          className="pick-row is-draggable"
          title={t("warRoom.chartPanel.dragTitle", { chart: form.label })}
        >
          <span className="pick-row__grip">
            <HolderOutlined />
          </span>
          <span className="pick-row__icon">{ICONS[form.type]}</span>
          <span className="pick-row__label">{form.label}</span>
          <span className="pick-row__hint">{form.job}</span>
        </div>
      ))}

      <Text type="secondary" className="panel-note">
        {t("warRoom.chartPanel.noDualAxis")}
      </Text>
    </div>
  );
}
